import { FormattedMessage } from 'react-intl';
import React, { useState } from 'react';
import {
  Paper,
  Typography,
  Box,
  Divider,
  IconButton,
  Tooltip,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CallSplitIcon from '@mui/icons-material/CallSplit';
import MessageIcon from '@mui/icons-material/Message';
import PublicIcon from '@mui/icons-material/Public';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import { useLocalization } from '@ui-framework/index';
import WIDGET_TEMPLATES from '../WidgetTemplates';

const WIDGET_ICONS = {
  TRIGGER: CallSplitIcon,
  SWITCH: CallSplitIcon,
  SEND_MESSAGE: MessageIcon,
  SEND_MESSAGE_AND_WAIT: MessageIcon,
  MAKE_HTTP_REQUEST: PublicIcon,
  PLAY: PlayCircleOutlineIcon,
  GATHER_INPUT_ON_CALL: PlayCircleOutlineIcon,
  SET_VARIABLE: PublicIcon,
  DIAL: MessageIcon,
  GENERIC: PublicIcon,
};

function WidgetItem({ type, label, icon: Icon, color, dragToAddLabel }) {
  function onDragStart(e, nodeType, nodeLabel) {
    const dt = e.dataTransfer;
    dt.setData('application/reactflow/type', nodeType);
    dt.setData('application/reactflow/label', nodeLabel);
    dt.effectAllowed = 'move';
  }
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        p: 1,
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        cursor: 'grab',
        transition: 'all 0.2s',
        '&:hover': {
          boxShadow: 2,
          borderColor: 'primary.light',
          '& .icon-box': { transform: 'scale(1.1)' },
        },
        '&:active': { cursor: 'grabbing' },
      }}
      onDragStart={event => onDragStart(event, type, label)}
      draggable>
      <Box
        className="icon-box"
        sx={{
          p: 1,
          borderRadius: 1,
          bgcolor: color,
          color: 'white',
          display: 'flex',
          transition: 'transform 0.2s',
        }}>
        <Icon sx={{ fontSize: 20 }} />
      </Box>
      <Box sx={{ flex: 1 }}>
        <Typography
          variant="body2"
          fontWeight="bold"
          color="text.primary"
          display="block">
          {label}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {dragToAddLabel}
        </Typography>
      </Box>
    </Box>
  );
}

function Sidebar() {
  const [collapsed, setCollapsed] = useState(false);
  const { formatMessage } = useLocalization();

  const expandWidgetsLabel = formatMessage({
    id: 'EXPAND_WIDGETS',
    defaultMessage: 'Expand Widgets',
  });
  const collapseWidgetsLabel = formatMessage({
    id: 'COLLAPSE_WIDGETS',
    defaultMessage: 'Collapse Widgets',
  });
  const widgetsTitle = formatMessage({
    id: 'WIDGETS',
    defaultMessage: 'Widgets',
  });
  const dragNodesHint = formatMessage({
    id: 'DRAG_AND_DROP_NODES_TO_THE_CANVAS',
    defaultMessage: 'Drag and drop nodes to the canvas',
  });
  const dragToAddLabel = formatMessage({
    id: 'DRAG_TO_ADD',
    defaultMessage: 'Drag to add',
  });
  const connectTip = formatMessage({
    id: 'FLOW_SIDEBAR_CONNECT_TIP',
    defaultMessage:
      'You can connect nodes by dragging from their bottom handles.',
  });

  if (collapsed) {
    return (
      <Box
        sx={{
          position: 'absolute',
          right: 0,
          top: 16,
          bottom: 16,
          width: 48,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
        }}>
        <Tooltip title={expandWidgetsLabel} placement="left">
          <IconButton
            color="primary"
            aria-label={expandWidgetsLabel}
            onClick={() => setCollapsed(false)}
            size="large">
            <ChevronLeftIcon />
          </IconButton>
        </Tooltip>
      </Box>
    );
  }

  return (
    <Paper
      elevation={6}
      sx={{
        position: 'absolute',
        right: 0,
        top: 16,
        bottom: 16,
        width: 320,
        maxWidth: 'min(320px, calc(100vw - 32px))',
        display: 'flex',
        flexDirection: 'column',
        zIndex: 1000,
        p: 2,
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(8px)',
        borderRadius: 2,
      }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <Tooltip title={collapseWidgetsLabel} placement="left">
          <IconButton
            color="primary"
            aria-label={collapseWidgetsLabel}
            onClick={() => setCollapsed(true)}
            size="small">
            <ChevronRightIcon />
          </IconButton>
        </Tooltip>
        <Typography
          variant="h6"
          fontWeight="bold"
          gutterBottom
          mb={0.5}
          sx={{ flex: 1 }}>
          {widgetsTitle}
        </Typography>
      </Box>
      <Typography
        variant="caption"
        color="text.secondary"
        mb={2}
        display="block">
        {dragNodesHint}
      </Typography>
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
          pr: 1,
        }}>
        {Object.entries(WIDGET_TEMPLATES).map(([type, template]) => (
          <WidgetItem
            key={type}
            type={type}
            label={template.label || type.replace(/_/g, ' ')}
            icon={WIDGET_ICONS[type] || PublicIcon}
            color={template.color || '#1976d2'}
            template={template}
            dragToAddLabel={dragToAddLabel}
          />
        ))}
      </Box>
      <Box sx={{ mt: 'auto', pt: 2 }}>
        <Divider sx={{ mb: 2 }} />
        <Box sx={{ bgcolor: 'info.selected', p: 1.5, borderRadius: 1 }}>
          <Typography variant="caption" color="info.main">
            <strong>
              <FormattedMessage id="TIP" defaultMessage="Tip" />:
            </strong>{' '}
            {connectTip}
          </Typography>
        </Box>
      </Box>
    </Paper>
  );
}

export default Sidebar;
